import type { OptionLookupAPI, TranslatedOptionItem } from '@/services/property'
import type { FieldItem, OptionItem } from '@/types'

// Collect only leaf options (those without nested options)
export function flattenLeafOptions(items?: OptionItem[]): OptionItem[] {
  if (!items || items.length === 0) return []
  const result: OptionItem[] = []
  const stack: OptionItem[] = [...items]
  while (stack.length) {
    const current = stack.pop() as OptionItem | undefined
    if (!current) continue
    const children = current.options?.items ?? []
    if (children.length > 0) {
      for (const child of children) stack.push(child)
    } else {
      result.push(current)
    }
  }
  return result
}

type OptionLike = Partial<
  Pick<OptionItem, 'id' | 'label' | 'value' | 'translations'>
>

export function safeLabelFromOption(o: OptionLike): string {
  return (
    (o?.label as string | undefined) ??
    (o?.translations?.items?.[0]?.label as string | undefined) ??
    (o?.value != null ? String(o.value) : undefined) ??
    String(o?.id ?? '')
  )
}

export function safeLabelFromField(f?: Partial<FieldItem>): string {
  return (
    (f?.translations?.items?.[0]?.label as string | undefined) ??
    (f?.label as string | undefined) ??
    (f?.key as string | undefined) ??
    String(f?.id ?? '')
  )
}

export function safeParseJson(jsonString?: string): string | undefined {
  try {
    return jsonString ? JSON.parse(jsonString) : jsonString
  } catch {
    return undefined
  }
}

export function createOptionLookupAPI(params: {
  optionsByID: Record<string, Record<string, TranslatedOptionItem>> | undefined
}): OptionLookupAPI | undefined {
  const { optionsByID } = params
  if (!optionsByID) return undefined

  return {
    getIdsForKey: (propertyKey) => {
      const byId = optionsByID[propertyKey]
      if (!byId) return undefined
      const ids = Object.keys(byId)
      return ids.length ? ids : undefined
    },
    getLabelByKey: (propertyKey, id) => {
      return optionsByID[propertyKey]?.[id]?.label
    },
  }
}

/**
 * Canonical `refData` builder used across the app.
 *
 * Works with:
 * - flat lists (no `options`)
 * - nested option trees (`options` children)
 */
export function buildRefDataById<
  T extends { id: string; label: string; options?: readonly T[] },
>(items: readonly T[] | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!items || items.length === 0) return out

  const stack: T[] = [...items]
  while (stack.length) {
    const current = stack.pop()
    if (!current) continue
    out[current.id] = current.label
    const children = current.options ?? []
    for (const child of children) stack.push(child)
  }

  return out
}
